import * as types from "../mutation-types";

const ReportConstant = {
    COVER_PAGE: "CoverPage",
    Investment_Details: "InvestmentDetail"
};

function initialState() {
    return {
        reportTitle: undefined,
        reportCover: {
            preparedForFirstName: "",
            preparedForLastName: "",
            preparedForAddress: "",
            preparedForCity: "",
            preparedForState: "",
            preparedForZipCode: "",
            preparedForCountry: "",
            preparedForPhone: "",
            preparedForEmail: "",
            preparedByFirstName: "",
            preparedByLastName: "",
            preparedByAddress: "",
            preparedByCity: "",
            preparedByState: "",
            preparedByZipCode: "",
            preparedByCountry: "",
            preparedByPhone: "",
            preparedByEmail: ""
        },
        reportGeneral: {
            reportLanguage: "en-US",
            reportFontSize: 0,
            reportDateFormat: "MMDDYYYY",
            releaseDate: 0,
            printOrientation: 0
        },
        includeMFComprehensive: false,
        reportOptions: [],
        stateList: []
    };
}

const state = {
    ...initialState()
};

const getters = {
    stateList(state, getters) {
        return state.stateList.map(s => ({
            text: s.stateFullName,
            value: s.stateFullName.toLowerCase()
        }));
    },
    adminToolReportOptions(state, getters, rootState) {
        const adminToolConfig = rootState.config.adminToolConfig;
        if (adminToolConfig !== null) {
            return adminToolConfig.reportOptions.map(i => ({
                reportId: i.reportId,
                labelId: i.labelKey,
                disabled: i.disabled,
                checked: i.checked,
                visible: i.visible
            }));
        }
        return [];
    },
    reportSetting(state, getters, rootState) {
        const reportOptions = getters.reportOptionsModel.map((r, index) => {
            return {
                order: index + 1,
                reportId: r.reportId,
                include: r.checked,
                settings: r.settings || {}
            };
        });
        return {
            hypoReportType: 0,
            hypoGraphType: 0,
            hypoGraphScaleType: 0,
            returnDisplayPeriod: 3,
            reportLanguage: state.reportGeneral.reportLanguage,
            fontSize: state.reportGeneral.reportFontSize,
            dateFormat: state.reportGeneral.reportDateFormat,
            releaseDateType: state.reportGeneral.releaseDate,
            pageOrientation: state.reportGeneral.printOrientation,
            reportOptions: reportOptions
        };
    },

    coverPageReportSettings: state => defaultCoverPageTitle => {
        return {
            reportTitle: typeof state.reportTitle === "undefined" ? defaultCoverPageTitle : state.reportTitle,
            ...state.reportCover
        };
    },

    idrReportSettings: (state, getters, rootState, rootGetters) => {
        if (rootGetters["config/showMFComprehensive"]) {
            return {
                includeMFComprehensive: state.includeMFComprehensive
            };
        }
        return null;
    },

    reportOptionsModel(state, getters) {
        return state.reportOptions.map(r => {
            let reportSettings = null;
            switch (r.reportId) {
                case ReportConstant.COVER_PAGE:
                    reportSettings = getters.coverPageReportSettings(r.settings.reportTitle);
                    break;
                case ReportConstant.Investment_Details:
                    reportSettings = getters.idrReportSettings;
                    break;
                default:
                    break;
            }
            if (reportSettings) {
                return {
                    ...r,
                    settings: reportSettings
                };
            }
            return r;
        });
    }
};

const actions = {
    loadReportSetting({ commit, getters }, sourceReportSetting) {
        const reportOptions = getters.adminToolReportOptions.map(r => {
            const currentReport = sourceReportSetting.find(d => d.reportId === r.reportId);

            let isCurrentReportIncluded = r.checked;
            if (currentReport !== undefined) {
                isCurrentReportIncluded = currentReport.include ? true : false;
            }

            if (r.disabled) {
                isCurrentReportIncluded = r.disabled;
            }

            return {
                ...r,
                checked: isCurrentReportIncluded,
                settings: currentReport && currentReport.settings ? currentReport.settings : {}
            };
        });
        commit(types.SET_REPORT_OPTIONS, reportOptions);
    },
    setReportCoverSetting({ commit }, coverSetting) {
        const reportCover = { ...state.reportCover, ...coverSetting };
        commit(types.SET_REPORT_COVER, reportCover);
    },
    setReportGeneralSetting({ commit }, generalSetting) {
        const reportGeneral = { ...state.reportGeneral, ...generalSetting };
        commit(types.SET_REPORT_GENERAL, reportGeneral);
    },
    getStateList({ commit }, { fetch, countryId = "USA" }) {
        return fetch({
            url: `/api/v1/Application/GetStateList?countryId=${countryId}`,
            method: "GET"
        }).then(data => {
            commit(types.SET_STATE_LIST, data);
        });
    },
    generatePDFReport({ rootGetters }, { fetch }) {
        return fetch({
            url: `/api/v1/Report`,
            method: "POST",
            body: JSON.stringify(rootGetters.hypoInput)
        }).then(response => response.arrayBuffer());
    }
};

const mutations = {
    [types.RESET](state) {
        const initialData = initialState();
        Object.keys(initialData).forEach(key => {
            state[key] = initialData[key];
        });
    },
    [types.SET_REPORT_OPTIONS](state, data) {
        state.reportOptions = data;
    },

    [types.SET_REPORT_COVER](state, data) {
        state.reportCover = data;
    },

    [types.SET_REPORT_GENERAL](state, data) {
        state.reportGeneral = data;
    },

    [types.SET_STATE_LIST](state, data) {
        state.stateList = data;
    },

    [types.SET_REPORT_TITLE](state, data) {
        state.reportTitle = data;
    },

    [types.SET_PREPARED_FOR_FIRST_NAME](state, data) {
        state.reportCover.preparedForFirstName = data;
    },

    [types.SET_PREPARED_FOR_LAST_NAME](state, data) {
        state.reportCover.preparedForLastName = data;
    },

    [types.SET_PREPARED_FOR_ADDRESS](state, data) {
        state.reportCover.preparedForAddress = data;
    },

    [types.SET_PREPARED_FOR_CITY](state, data) {
        state.reportCover.preparedForCity = data;
    },

    [types.SET_PREPARED_FOR_STATE](state, data) {
        state.reportCover.preparedForState = data;
    },

    [types.SET_PREPARED_FOR_ZIP_CODE](state, data) {
        state.reportCover.preparedForZipCode = data;
    },

    [types.SET_PREPARED_FOR_COUNTRY](state, data) {
        state.reportCover.preparedForCountry = data;
    },

    [types.SET_PREPARED_FOR_PHONE](state, data) {
        state.reportCover.preparedForPhone = data;
    },

    [types.SET_PREPARED_FOR_EMAIL](state, data) {
        state.reportCover.preparedForEmail = data;
    },

    [types.SET_PREPARED_BY_FIRST_NAME](state, data) {
        state.reportCover.preparedByFirstName = data;
    },

    [types.SET_PREPARED_BY_LAST_NAME](state, data) {
        state.reportCover.preparedByLastName = data;
    },

    [types.SET_PREPARED_BY_ADDRESS](state, data) {
        state.reportCover.preparedByAddress = data;
    },

    [types.SET_PREPARED_BY_CITY](state, data) {
        state.reportCover.preparedByCity = data;
    },

    [types.SET_PREPARED_BY_STATE](state, data) {
        state.reportCover.preparedByState = data;
    },

    [types.SET_PREPARED_BY_ZIP_CODE](state, data) {
        state.reportCover.preparedByZipCode = data;
    },

    [types.SET_PREPARED_BY_COUNTRY](state, data) {
        state.reportCover.preparedByCountry = data;
    },

    [types.SET_PREPARED_BY_PHONE](state, data) {
        state.reportCover.preparedByPhone = data;
    },

    [types.SET_PREPARED_BY_EMAIL](state, data) {
        state.reportCover.preparedByEmail = data;
    },

    [types.SET_INCLUDE_MF_COMPREHENSIVE](state, data) {
        state.includeMFComprehensive = data;
    }
};

export default {
    state,
    actions,
    getters,
    mutations
};
